
import './style.scss';
import { Add, ContentCopy, Delete, Edit, FilterAlt, Refresh, Warning } from "@mui/icons-material";
import { Avatar, Box, Grid, IconButton } from "@mui/material";
import DataGrid from '../../../../components/DataGrid';
import ContentTitle from "../../../../components/Content/ContentTitle";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IUser from "../../../../interfaces/IUser";
import { useEffect, useCallback } from "react";
import UserService from "../../../../services/UserService";
import FormHelper from "../../../../helpers/FormHelper";
import RegistrationStatus from "../../../../components/RegistrationStatus";
import AddEditDialog from "./AddEditDialog";
import { useAuthContext } from "../../../../contexts/AuthContext";
import RemoveDialog from "./RemoveDialog";
import DefaultConstants from "../../../../data/Constants";
import { INavigationLink } from "../../../../interfaces/INavigation";
import Filters from './Filters';
import { RegistrationStatusEnum } from '../../../../data/Enums';
import IEnterprise from '../../../../interfaces/IEnterprise';
import EnterpriseService from '../../../../services/EnterpriseService';
import Tooltip from '../../../../components/Tooltip';
import useDrawer from '../../../../hooks/useDrawer';
import useDialog from '../../../../hooks/useDialog';
import useState from '../../../../hooks/useState';


interface IStateValues {
    filters: FormData | undefined;
    data: IUser[];
    selectedRow: IUser | undefined;
    enterprises: IEnterprise[];
}


const User = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { user, hasPermission, enterprise } = useAuthContext();

    const state = useState<IStateValues>({
        filters: undefined,
        data: [],
        selectedRow: undefined,
        enterprises: []
    });

    const filtersDrawer = useDrawer();
    const addEditDialog = useDialog();
    const removeDialog = useDialog();

    useEffect(() => {
        handleRefresh();

        if (state.values.enterprises.length === 0) {
            const formData = FormHelper.Create(undefined);
            EnterpriseService.List(formData)
                .then(resp => {
                    state.update('enterprises', resp.data.enterprises);
                });
        }

        // eslint-disable-next-line
    }, [state.values.filters]);
    
    
    const handleRefresh = useCallback(() => {
        let formData = state.values.filters ?? FormHelper.Create(undefined);

        if (!state.values.filters && enterprise) {
            formData = FormHelper.Add(formData, 'enterprise', enterprise.id)
        }

        UserService.List(formData)
            .then((resp) => {
                state.update('data', resp.data.users);
            });
        // eslint-disable-next-line
    }, [state.values.filters]);


    const handleAddEdit = useCallback((row: IUser | undefined) => {
        state.update('selectedRow', row);
        addEditDialog.handleOpen();
        // eslint-disable-next-line
    }, []);


    const handleRemove = useCallback((row: IUser) => {
        state.update('selectedRow', row);
        removeDialog.handleOpen();
        // eslint-disable-next-line
    }, []);


    const dataGridColumns = [
        { 
            field: 'avatar', 
            sortable: false, 
            headerName: '', 
            width: 80,
            renderCell: (params: any) => {
                return <Box className='width-100pc display-flex justifycontent-center'>
                    <Avatar
                        alt={params.row.fullName.toUpperCase()} 
                        src={params.row.avatar} 
                        className='fontsize-14'
                    />
                </Box>;
            }
        }, { 
            field: 'fullName', 
            headerName: translate('fullName'),
            flex: 1, 
            minWidth: 200
        }, { 
            field: 'login', 
            headerName: translate('user'),
            flex: 1, 
            minWidth: 120
        }, { 
            field: 'email', 
            headerName: translate('email'),
            flex: 1, 
            minWidth: 320
        }, { 
            field: 'partnerId',  
            headerName: translate('partnerId'), 
            flex: 1, 
            minWidth: 200,
            renderCell: (params: any) => {
                if (!params.row.partnerId) {
                    if (params.row.administrator) {
                        return translate('undefined');
                    }

                    return <Tooltip title={translate('_userPartnerIdEmpty')} placement="bottom">
                        <IconButton className='actionbutton-warning marginX-none'>
                            <Warning fontSize='small' />
                        </IconButton>
                    </Tooltip>;
                }
                
                return params.row.partnerId;
            }
        }, { 
            field: 'registrationStatusId', 
            type: DefaultConstants.dataGridRegistrationStatusType, 
            headerName: translate('situation'), 
            flex: 1, 
            minWidth: 200, 
            renderCell: (params: any) => {
                return <RegistrationStatus id={params.row.registrationStatusId} />;
            }
        }, { 
            field: 'actions', 
            type: 'actions', 
            headerName: translate('actions'), 
            width: 200, 
            renderCell: (params: any) => {
                if (params.row.administrator && !user?.administrator) {
                    return;
                }

                if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                    return;
                }

                return (
                    <Grid item>
                        {
                            hasPermission(`${linkProps.alias[0]}.Add`) && (
                                <Tooltip title={translate('clone')}>
                                    <IconButton 
                                        className='actionbutton-clone' 
                                        onClick={() => handleAddEdit({
                                            ...params.row, 
                                            id: 0,
                                            fullName: '',
                                            login: '',
                                            email: '',
                                            token: '',
                                        })}
                                    >
                                        <ContentCopy fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {
                            hasPermission(`${linkProps.alias[0]}.Edit`) && (
                                <Tooltip title={translate('edit')}>
                                    <IconButton className='actionbutton-edit' onClick={() => handleAddEdit(params.row)}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {
                            !params.row.administrator && hasPermission(`${linkProps.alias[0]}.Remove`) && (
                                <Tooltip title={translate('remove')}>
                                    <IconButton className='actionbutton-remove' onClick={() => handleRemove(params.row)}>
                                        <Delete fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    </Grid>
                );
            }
        }
    ];

    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle {...linkProps} />
                </Grid>

                <Grid item className='grid-tools'>
                    {
                        hasPermission(`${linkProps.alias[0]}.Add`) && (
                            <Tooltip title={translate('add')}>
                                <IconButton onClick={() => handleAddEdit(undefined)}>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                    
                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filtersDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid container className='grid-content'>
                <Grid item xs={12} className='paper-content'>
                    <DataGrid 
                        columns={dataGridColumns} 
                        data={state.values.data} 
                        openSearch={false}
                        onCellDoubleClick={params => {
                            if (params.row.administrator && !user?.administrator) {
                                return;
                            }

                            if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                                return;
                            }

                            if (hasPermission(`${linkProps.alias[0]}.Edit`)) {
                                handleAddEdit(params.row);
                                return;
                            }
                            
                            if (hasPermission(`${linkProps.alias[0]}.Remove`)) {
                                handleRemove(params.row);
                                return;
                            }
                            
                            handleAddEdit(params.row);
                        }}
                    />
                </Grid>
            </Grid>

            <Filters
                open={filtersDrawer.open}
                handleClose={() => filtersDrawer.handleClose()}
                handleApplyFilters={(formData) => state.update('filters', formData)}
                enterprises={state.values.enterprises}
            />

            <AddEditDialog
                open={addEditDialog.open}
                handleClose={() => addEditDialog.handleClose()}
                data={state.values.selectedRow}
                handleListRefresh={handleRefresh}
                enterprises={state.values.enterprises}
            />

            <RemoveDialog
                open={removeDialog.open}
                handleClose={() => removeDialog.handleClose()}
                data={state.values.selectedRow}
                handleListRefresh={handleRefresh}
            />
        </>
    );
}

export default User;